import React, { useState, useRef, useEffect } from "react";
import { useTheme } from "../../contexts/ThemeContext";

export interface DropdownMenuItem {
  label: string;
  icon?: React.ReactNode;
  onClick: () => void;
  variant?: "default" | "danger" | "success" | "warning";
  disabled?: boolean;
  divider?: boolean;
}

interface DropdownMenuProps {
  items: DropdownMenuItem[];
  trigger?: React.ReactNode;
  align?: "left" | "right";
  disabled?: boolean;
}

const DropdownMenu: React.FC<DropdownMenuProps> = ({
  items,
  trigger,
  align = "right",
  disabled = false,
}) => {
  const { isDarkMode } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<{
    top: number;
    left: number;
    right?: number;
    placement: "top" | "bottom";
  } | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  // Calculate position when opening
  useEffect(() => {
    if (isOpen && triggerRef.current) {
      const updatePosition = () => {
        if (!triggerRef.current) return;

        const rect = triggerRef.current.getBoundingClientRect();
        const dropdownHeight = 300; // Altura estimada del menú
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;

        // Decidir si mostrar arriba o abajo
        const showAbove =
          spaceBelow < dropdownHeight && spaceAbove > spaceBelow;

        const position: typeof dropdownPosition = {
          top: showAbove
            ? Math.max(10, rect.top - 8) // Justo encima del botón con pequeño margen
            : rect.bottom + 8,
          left: align === "left" ? rect.left : undefined,
          right: align === "right" ? window.innerWidth - rect.right : undefined,
          placement: showAbove ? "top" : "bottom",
        };

        setDropdownPosition(position);
      };

      updatePosition();

      // Actualizar posición al hacer scroll
      const handleScroll = () => {
        updatePosition();
      };

      window.addEventListener("scroll", handleScroll, true);
      window.addEventListener("resize", handleScroll);

      return () => {
        window.removeEventListener("scroll", handleScroll, true);
        window.removeEventListener("resize", handleScroll);
      };
    }
  }, [isOpen, align]);

  const handleItemClick = (item: DropdownMenuItem) => {
    if (!item.disabled) {
      item.onClick();
      setIsOpen(false);
    }
  };

  const getItemClasses = (item: DropdownMenuItem) => {
    const baseClasses =
      "flex items-center gap-3 px-4 py-2.5 text-sm transition-colors";

    if (item.disabled) {
      return `${baseClasses} opacity-50 cursor-not-allowed ${
        isDarkMode ? "text-gray-500" : "text-gray-400"
      }`;
    }

    switch (item.variant) {
      case "danger":
        return `${baseClasses} ${
          isDarkMode
            ? "text-red-400 hover:bg-red-900/20"
            : "text-red-600 hover:bg-red-50"
        } cursor-pointer`;
      case "success":
        return `${baseClasses} ${
          isDarkMode
            ? "text-green-400 hover:bg-green-900/20"
            : "text-green-600 hover:bg-green-50"
        } cursor-pointer`;
      case "warning":
        return `${baseClasses} ${
          isDarkMode
            ? "text-yellow-400 hover:bg-yellow-900/20"
            : "text-yellow-600 hover:bg-yellow-50"
        } cursor-pointer`;
      default:
        return `${baseClasses} ${
          isDarkMode
            ? "text-gray-200 hover:bg-gray-700"
            : "text-gray-700 hover:bg-gray-50"
        } cursor-pointer`;
    }
  };

  const defaultTrigger = (
    <button
      className={`p-2 rounded-lg transition-colors ${
        isDarkMode
          ? "text-gray-400 hover:bg-gray-700 hover:text-gray-200"
          : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
      } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
      disabled={disabled}
    >
      <svg
        className="w-5 h-5"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"
        />
      </svg>
    </button>
  );

  return (
    <div className="relative inline-block" ref={triggerRef}>
      {/* Trigger Button */}
      <div onClick={() => !disabled && setIsOpen(!isOpen)}>
        {trigger || defaultTrigger}
      </div>

      {/* Dropdown Menu - Portal con posicionamiento fijo */}
      {isOpen && dropdownPosition && (
        <div
          ref={dropdownRef}
          className={`fixed z-[9999] min-w-[200px] rounded-lg shadow-xl border overflow-hidden ${
            isDarkMode
              ? "bg-gray-800 border-gray-700"
              : "bg-white border-gray-200"
          }`}
          style={{
            top:
              dropdownPosition.placement === "top"
                ? "auto"
                : `${dropdownPosition.top}px`,
            bottom:
              dropdownPosition.placement === "top"
                ? `${window.innerHeight - dropdownPosition.top}px`
                : "auto",
            left:
              dropdownPosition.left !== undefined
                ? `${dropdownPosition.left}px`
                : "auto",
            right:
              dropdownPosition.right !== undefined
                ? `${dropdownPosition.right}px`
                : "auto",
            animation: `${
              dropdownPosition.placement === "top" ? "fadeInUp" : "fadeInDown"
            } 0.15s ease-out`,
            transformOrigin:
              dropdownPosition.placement === "top" ? "bottom" : "top",
            maxHeight: "400px",
            overflowY: "auto",
          }}
        >
          {items.map((item, index) => (
            <React.Fragment key={index}>
              {item.divider ? (
                <div
                  className={`my-1 border-t ${
                    isDarkMode ? "border-gray-700" : "border-gray-200"
                  }`}
                />
              ) : (
                <div
                  className={getItemClasses(item)}
                  onClick={() => handleItemClick(item)}
                >
                  {item.icon && (
                    <span className="flex-shrink-0 w-5 h-5">{item.icon}</span>
                  )}
                  <span className="flex-1 text-left whitespace-nowrap">
                    {item.label}
                  </span>
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
      )}

      {/* CSS for animations */}
      <style>{`
        @keyframes fadeInDown {
          from {
            opacity: 0;
            transform: translateY(-8px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(8px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default DropdownMenu;
